import React, { useEffect, useState } from 'react';
import { handoverAPI, analysisAPI } from '../services/api';
import { formatCurrency as fmt } from '../utils/format';

const MODES = [
  { value: 'cash', label: '💵 Cash in Hand', desc: 'Physical cash dena' },
  { value: 'upi', label: '📱 UPI Transfer', desc: 'GPay / PhonePe / Paytm' },
  { value: 'bank_transfer', label: '🏦 Bank Transfer', desc: 'NEFT / IMPS' }
];

const STATUS_STYLE = {
  pending:   { bg: '#fffbeb', color: '#b45309', label: '⏳ Confirmation Pending' },
  received:  { bg: '#f0fdf4', color: '#16a34a', label: '✅ Owner ne receive kiya' },
  confirmed: { bg: '#f0fdf4', color: '#16a34a', label: '✅ Confirmed' },
  disputed:  { bg: '#fef2f2', color: '#dc2626', label: '⚠️ Disputed' }
};

export default function CashHandover() {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState({ totalSent: 0, pendingAmount: 0 });
  const [transparency, setTransparency] = useState(null);
  const [cashInHand, setCashInHand] = useState(0);
  const [form, setForm] = useState({ amount: '', mode: 'cash', referenceNo: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ msg: '', type: 'success' });
  const [step, setStep] = useState('list'); // 'list' | 'form' | 'confirm'

  const load = async () => {
    const [handRes, anlRes] = await Promise.all([
      handoverAPI.list(),
      analysisAPI.get({ period: 'month', days: 7 }).catch(() => null)
    ]);
    setList(handRes.data.handovers || []);
    setSummary({
      totalSent: handRes.data.totalSent || 0,
      pendingAmount: handRes.data.pendingAmount || 0
    });
    const t = handRes.data.transparency || null;
    setTransparency(t);
    const todayIn =
      handRes.data.cashInHandToday ??
      anlRes?.data?.analysis?.cash?.cashInHandToday ??
      t?.today?.totalIn ??
      0;
    setCashInHand(todayIn);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const flash = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 4000);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await handoverAPI.create({ ...form, amount: Number(form.amount) });
      setForm({ amount: '', mode: 'cash', referenceNo: '', notes: '' });
      setStep('list');
      await load();
      flash('✅ Handover recorded! Owner ko confirm karne ka notification gaya.');
    } catch (err) {
      flash(err.response?.data?.message || 'Save failed. Please retry.', 'danger');
      setStep('form');
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = list.filter(h => h.status === 'pending').length;
  const lastHandover = list[0];
  const daysSinceLast = lastHandover
    ? Math.floor((Date.now() - new Date(lastHandover.handoverDate || lastHandover.createdAt)) / (1000 * 60 * 60 * 24))
    : null;

  const tm = transparency?.thisMonth;
  const rc = transparency?.reconciliation;
  const suggestSettle = tm?.approxStillToSettle ?? 0;

  const openHandoverForm = (amountHint) => {
    const n = Number(amountHint);
    setForm({
      amount: n > 0 ? Math.round(n) : '',
      mode: 'cash',
      referenceNo: '',
      notes: ''
    });
    setStep('form');
  };

  return (
    <div>
      {/* Toast */}
      {toast.msg && (
        <div className={`alert-soft ${toast.type} mb-3`}>{toast.msg}</div>
      )}

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h4 className="mb-0 fw-700">Cash Handover</h4>
          <div className="text-muted" style={{ fontSize: 13 }}>
            Owner ko cash bhejo weekly · Har handover track hoga
          </div>
        </div>
        {step === 'list' && (
          <button
            className="btn btn-success"
            onClick={() => openHandoverForm(suggestSettle > 0 ? suggestSettle : cashInHand)}
          >
            + New Handover
          </button>
        )}
        {step !== 'list' && (
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setStep('list')}>
            ← Cancel
          </button>
        )}
      </div>

      {/* ── Summary tiles ── */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #7c3aed' }}>
            <div className="stat-label">Cash in hand (today)</div>
            <div className="stat-value" style={{ color: '#7c3aed' }}>{fmt(cashInHand)}</div>
            <div className="stat-sub">Aaj ki collections</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: '4px solid #16a34a' }}>
            <div className="stat-label">Total sent (all time)</div>
            <div className="stat-value" style={{ color: '#16a34a' }}>{fmt(summary.totalSent)}</div>
            <div className="stat-sub">{list.length} transactions</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: `4px solid ${summary.pendingAmount > 0 ? '#b45309' : '#e5e7eb'}` }}>
            <div className="stat-label">Pending confirmation</div>
            <div className="stat-value" style={{ color: summary.pendingAmount > 0 ? '#b45309' : '#6b7280' }}>
              {fmt(summary.pendingAmount)}
            </div>
            <div className="stat-sub">{pendingCount} transaction{pendingCount !== 1 ? 's' : ''} pending</div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="stat-tile" style={{ borderLeft: `4px solid ${daysSinceLast !== null && daysSinceLast > 7 ? '#dc2626' : '#e5e7eb'}` }}>
            <div className="stat-label">Last handover</div>
            <div className="stat-value" style={{ color: daysSinceLast !== null && daysSinceLast > 7 ? '#dc2626' : '#1f2937', fontSize: 18 }}>
              {daysSinceLast !== null ? `${daysSinceLast}d ago` : 'N/A'}
            </div>
            {daysSinceLast !== null && daysSinceLast > 7 && (
              <div className="stat-sub" style={{ color: '#dc2626' }}>⚠️ Weekly bhejo!</div>
            )}
          </div>
        </div>
      </div>

      {/* ── Cash clarity (system record vs handover) ── */}
      {transparency && step === 'list' && (
        <div className="card mb-4" style={{ border: '2px solid #0d6efd', background: '#f8fafc' }}>
          <div className="card-body py-3">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
              <div>
                <div className="fw-700" style={{ fontSize: 16, color: '#1e3a5f' }}>📒 Cash clarity — record ke hisaab se</div>
                <div className="text-muted" style={{ fontSize: 12 }}>{transparency.disclaimer}</div>
              </div>
              {suggestSettle > 0 && (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => openHandoverForm(suggestSettle)}>
                  {fmt(suggestSettle)} handover suggest
                </button>
              )}
            </div>

            <div className="row g-2 mb-3">
              <div className="col-12 col-md-6">
                <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
                  <div className="fw-700 mb-2" style={{ fontSize: 13, color: '#334155' }}>Aaj (system ne track kiya)</div>
                  <div className="d-flex justify-content-between" style={{ fontSize: 12 }}><span>Cash / UPI sales</span><strong>{fmt(transparency.today.cashUpiSales)}</strong></div>
                  <div className="d-flex justify-content-between" style={{ fontSize: 12 }}><span>EMI down (aaj book)</span><strong>{fmt(transparency.today.emiDown)}</strong></div>
                  <div className="d-flex justify-content-between" style={{ fontSize: 12 }}><span>EMI collection</span><strong>{fmt(transparency.today.emiInstallments)}</strong></div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between fw-700"><span>Total aaj</span><span style={{ color: '#0d6efd' }}>{fmt(transparency.today.totalIn)}</span></div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #e2e8f0' }}>
                  <div className="fw-700 mb-2" style={{ fontSize: 13, color: '#334155' }}>Is mahine (business vs owner)</div>
                  <div className="d-flex justify-content-between" style={{ fontSize: 12 }}><span>Business cash in (record)</span><strong style={{ color: '#16a34a' }}>{fmt(tm.totalBusinessIn)}</strong></div>
                  <div className="d-flex justify-content-between text-muted" style={{ fontSize: 11 }}><span>· Cash/UPI sales</span><span>{fmt(tm.cashUpiSales)}</span></div>
                  <div className="d-flex justify-content-between text-muted" style={{ fontSize: 11 }}><span>· EMI down</span><span>{fmt(tm.emiDown)}</span></div>
                  <div className="d-flex justify-content-between text-muted" style={{ fontSize: 11 }}><span>· EMI installments</span><span>{fmt(tm.emiInstallments)}</span></div>
                  <div className="d-flex justify-content-between mt-1" style={{ fontSize: 12 }}><span>Aapne handover record kiya (is mahine)</span><strong>{fmt(tm.handoverYouRecorded)}</strong></div>
                  <div className="d-flex justify-content-between" style={{ fontSize: 12 }}><span>Owner ne confirm kiya — receipt date is mahine</span><strong style={{ color: '#16a34a' }}>{fmt(tm.handoverOwnerConfirmed)}</strong></div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between fw-700" style={{ fontSize: 14 }}>
                    <span>Ab owner ko dena (system — carry + sab)</span>
                    <span style={{ color: suggestSettle > 0 ? '#b45309' : '#16a34a' }}>{fmt(suggestSettle)}</span>
                  </div>
                  <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                    Poora hisaab neeche line-by-line. Personal cash mix ho to khud adjust karein.
                  </div>
                </div>
              </div>
            </div>

            {rc && (
              <div
                className="mt-3"
                style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', border: '1px solid #c7d2fe' }}
              >
                <div className="fw-700 mb-2" style={{ fontSize: 14, color: '#312e81' }}>
                  📊 Poora trail — kahan se kitna aaya, kitna dena banta hai
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-6">
                    <div className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                      <span>Pichle mahine tak bacha (opening carry)</span>
                      <strong style={{ color: '#b45309' }}>{fmt(rc.carryForwardOpening)}</strong>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                      <span>+ Is mahine business (record, aaj tak)</span>
                      <strong className="text-success">{fmt(rc.businessAddedThisMonthToDate)}</strong>
                    </div>
                    <div className="d-flex justify-content-between" style={{ fontSize: 12 }}>
                      <span>− Owner confirm (receipt, aaj tak)</span>
                      <strong className="text-danger">−{fmt(rc.ownerConfirmedThisMonthToDate)}</strong>
                    </div>
                    <hr className="my-2" />
                    <div className="d-flex justify-content-between fw-700" style={{ fontSize: 14 }}>
                      <span>= Ab dena (net)</span>
                      <span style={{ color: rc.netOwedToOwnerNow > 0 ? '#b45309' : '#16a34a' }}>{fmt(rc.netOwedToOwnerNow)}</span>
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="text-muted mb-1" style={{ fontSize: 11 }}>
                      <strong>{rc.prevMonth?.label}</strong> (sirf us mahine ki window): business{' '}
                      <strong>{fmt(rc.prevMonth?.businessIn)}</strong>
                      , owner confirm <strong>{fmt(rc.prevMonth?.ownerConfirmedInMonth)}</strong>
                      , us window me <strong>{fmt(rc.prevMonth?.netUnsettledInMonthWindow)}</strong> bacha dikhta hai.
                      <span className="d-block mt-1">Opening carry upar poora cumulative gap dikhata hai (pehle ke mahine bhi).</span>
                    </div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      Life-to-date business (record): <strong>{fmt(rc.businessRecordedLifeToDate)}</strong>
                      {' · '}
                      Life-to-date owner confirm: <strong>{fmt(rc.ownerConfirmedLifeToDate)}</strong>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ fontSize: 12, color: '#64748b' }}>
              All-time owner confirmed: <strong>{fmt(transparency.allTime?.handoverOwnerConfirmed || 0)}</strong>
            </div>
          </div>
        </div>
      )}

      {/* ── Alert: overdue handover ── */}
      {daysSinceLast !== null && daysSinceLast > 7 && step === 'list' && (
        <div className="alert-soft warning mb-3 d-flex justify-content-between align-items-center">
          <span>
            ⚠️ {daysSinceLast} din se koi handover nahi. Owner weekly cash chahte hain.
          </span>
          <button
            className="btn btn-sm btn-warning"
            onClick={() => openHandoverForm(suggestSettle > 0 ? suggestSettle : cashInHand)}
          >
            Abhi bhejo
          </button>
        </div>
      )}

      {/* ── How it works (first time explainer) ── */}
      {list.length === 0 && step === 'list' && (
        <div className="card mb-4" style={{ border: '1px solid #e0e7ff', background: '#f5f3ff' }}>
          <div className="card-body py-3">
            <div className="fw-700 mb-2" style={{ color: '#4338ca' }}>💡 Cash Handover kaise kaam karta hai?</div>
            <div className="row g-2" style={{ fontSize: 13 }}>
              {[
                { n: '1', t: 'Aap record karo', d: 'Kitna cash / UPI bhej rahe ho - enter karo' },
                { n: '2', t: 'Owner ko jayega', d: 'Notification milegi, confirm karte hain' },
                { n: '3', t: 'Confirmed!', d: 'Status "Received" ho jata hai - record mein save' }
              ].map(s => (
                <div className="col-md-4" key={s.n}>
                  <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', borderLeft: '3px solid #7c3aed' }}>
                    <strong style={{ color: '#7c3aed' }}>Step {s.n}:</strong> {s.t}
                    <div className="text-muted" style={{ fontSize: 11 }}>{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── NEW HANDOVER FORM ── */}
      {step === 'form' && (
        <div className="card mb-4" style={{ border: '1px solid #bbf7d0', background: '#f0fdf4' }}>
          <div className="card-body p-4">
            <h5 className="fw-700 mb-1" style={{ color: '#166534' }}>💸 Naya Handover Record karo</h5>
            <p className="text-muted mb-4" style={{ fontSize: 13 }}>
              Aaj portal ne track kiya: <strong style={{ color: '#16a34a' }}>{fmt(cashInHand)}</strong>
              {suggestSettle > 0 && (
                <> · Settle suggest (carry + sab): <strong style={{ color: '#b45309' }}>{fmt(suggestSettle)}</strong></>
              )}
            </p>

            {/* Mode selection */}
            <div className="mb-4">
              <label className="form-label fw-700">Transfer ka tarika</label>
              <div className="row g-2">
                {MODES.map(m => (
                  <div className="col-12 col-md-4" key={m.value}>
                    <div
                      onClick={() => setForm({ ...form, mode: m.value })}
                      style={{
                        border: `2px solid ${form.mode === m.value ? '#16a34a' : '#e5e7eb'}`,
                        borderRadius: 10,
                        padding: '12px 16px',
                        cursor: 'pointer',
                        background: form.mode === m.value ? '#f0fdf4' : '#fff',
                        transition: 'all 0.15s'
                      }}
                    >
                      <div className="fw-700" style={{ fontSize: 14 }}>{m.label}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{m.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label fw-700">Amount (₹)</label>
                <div className="input-group">
                  <span className="input-group-text">₹</span>
                  <input
                    type="number"
                    className="form-control form-control-lg"
                    placeholder="Kitna bhej rahe ho"
                    min={1}
                    value={form.amount}
                    onChange={e => setForm({ ...form, amount: e.target.value })}
                  />
                </div>
                {/* Quick presets */}
                <div className="d-flex gap-2 mt-2 flex-wrap align-items-center">
                  {suggestSettle > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      style={{ fontSize: 11 }}
                      onClick={() => setForm({ ...form, amount: Math.round(suggestSettle) })}
                    >
                      Record: {fmt(suggestSettle)} (net owed)
                    </button>
                  )}
                  {cashInHand > 0 && [cashInHand, Math.round(cashInHand * 0.75), Math.round(cashInHand * 0.5)].map((preset, i) => (
                    <button
                      key={i}
                      type="button"
                      className="btn btn-sm btn-outline-success"
                      style={{ fontSize: 11 }}
                      onClick={() => setForm({ ...form, amount: Math.round(preset) })}
                    >
                      {i === 0 ? 'Aaj full' : `${[75, 50][i - 1]}%`} — {fmt(preset)}
                    </button>
                  ))}
                </div>
              </div>

              {form.mode !== 'cash' && (
                <div className="col-md-6">
                  <label className="form-label fw-700">Transaction / Reference No.</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="UTR / Txn ID"
                    value={form.referenceNo}
                    onChange={e => setForm({ ...form, referenceNo: e.target.value })}
                  />
                </div>
              )}
            </div>

            {/* Note */}
            <div className="mb-4">
              <label className="form-label fw-700">Note (optional)</label>
              <input
                type="text"
                className="form-control"
                placeholder="Koi remark… jaise 'April week 1 handover'"
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            {/* Summary before submit */}
            {form.amount > 0 && (
              <div style={{ background: '#fff', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 18px', marginBottom: 16 }}>
                <div className="fw-700 mb-2" style={{ color: '#166534' }}>Summary</div>
                <div className="d-flex justify-content-between mb-1" style={{ fontSize: 14 }}>
                  <span>Transfer amount</span>
                  <strong style={{ color: '#16a34a', fontSize: 18 }}>{fmt(Number(form.amount))}</strong>
                </div>
                <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
                  <span>Mode</span>
                  <span>{MODES.find(m => m.value === form.mode)?.label}</span>
                </div>
                {form.referenceNo && (
                  <div className="d-flex justify-content-between" style={{ fontSize: 13 }}>
                    <span>Reference</span>
                    <span>{form.referenceNo}</span>
                  </div>
                )}
              </div>
            )}

            <div className="d-flex gap-3">
              <button
                className="btn btn-success btn-lg flex-grow-1"
                disabled={saving || !form.amount || Number(form.amount) < 1}
                onClick={submit}
              >
                {saving ? 'Recording…' : `✅ ${fmt(Number(form.amount) || 0)} Record karo`}
              </button>
              <button className="btn btn-outline-secondary btn-lg" onClick={() => setStep('list')}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pending handovers alert ── */}
      {summary.pendingAmount > 0 && step === 'list' && (
        <div className="alert-soft warning mb-3">
          ⏳ <strong>{fmt(summary.pendingAmount)}</strong> pending confirmation ke liye. Owner ne abhi receive nahi kiya.
          Unhe remind kar sakte ho.
        </div>
      )}

      {/* ── Handover History ── */}
      {step === 'list' && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span className="fw-700">Handover History</span>
            <span className="text-muted" style={{ fontSize: 12 }}>{list.length} records</span>
          </div>

          {list.length === 0 ? (
            <div className="card-body text-center py-5 text-muted">
              <div style={{ fontSize: 40 }}>📭</div>
              <div className="mt-2">Abhi koi handover record nahi hai.</div>
              <button className="btn btn-success mt-3" onClick={() => { setForm({ amount: '', mode: 'cash', referenceNo: '', notes: '' }); setStep('form'); }}>
                Pehla handover record karo
              </button>
            </div>
          ) : (
            <div>
              {list.map((h, i) => {
                const st = STATUS_STYLE[h.status] || STATUS_STYLE.pending;
                const daysAgo = Math.floor((Date.now() - new Date(h.handoverDate || h.createdAt)) / (1000 * 60 * 60 * 24));
                return (
                  <div
                    key={h._id}
                    style={{
                      padding: '16px 20px',
                      borderBottom: i < list.length - 1 ? '1px solid #f3f4f6' : 'none',
                      background: h.status === 'pending' ? '#fffdf5' : '#fff'
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 18, color: '#1f2937' }}>{fmt(h.amount)}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                          {new Date(h.handoverDate || h.createdAt).toLocaleDateString('en-IN', {
                            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
                          })}
                          <span className="ms-2">· {daysAgo === 0 ? 'Aaj' : `${daysAgo}d pehle`}</span>
                          {h.mode && <span className="ms-2">· {h.mode === 'cash' ? '💵 Cash' : h.mode === 'upi' ? '📱 UPI' : '🏦 Bank'}</span>}
                          {h.referenceNo && <span className="ms-2">· Ref: {h.referenceNo}</span>}
                        </div>
                        {h.notes && (
                          <div style={{ fontSize: 12, color: '#4b5563', marginTop: 4, fontStyle: 'italic' }}>"{h.notes}"</div>
                        )}
                        {h.status === 'received' && h.receivedAt && (
                          <div style={{ fontSize: 11, color: '#16a34a', marginTop: 3 }}>
                            ✅ Owner ne receive kiya: {new Date(h.receivedAt).toLocaleDateString('en-IN')}
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          padding: '4px 14px',
                          borderRadius: 20,
                          fontSize: 12,
                          fontWeight: 700,
                          background: st.bg,
                          color: st.color,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {st.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
